import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  ViewChild,
  ElementRef,
  inject,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Lesson, Module } from 'core/models/course-content.model';
import { LessonHeader } from 'components/lesson-header/lesson-header';

@Component({
  selector: 'app-pdf-content',
  standalone: true,
  imports: [CommonModule, LessonHeader],
  templateUrl: './pdf.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'w-full h-full flex flex-col',
  },
})
export class Pdf {
  private readonly sanitizer = inject(DomSanitizer);

  @Input({ required: true }) lesson!: Lesson;
  @Input() module?: Module | null;

  @ViewChild('pdfIframe', { static: false })
  pdfIframe!: ElementRef<HTMLIFrameElement>;

  protected get safePdfUrl(): SafeResourceUrl | null {
    if (!this.lesson?.pdfUrl) {
      return null;
    }

    // Adicionar parâmetros para exibir o PDF com barra lateral de navegação
    // Os parâmetros do PDF viewer são passados como fragmentos na URL (depois do #)
    // toolbar=0: oculta barra de ferramentas superior
    // navpanes=1: exibe painéis laterais (thumbnails de páginas)
    // scrollbar=0: oculta barra de rolagem interna
    // view=FitH: ajusta visualização para largura da página
    const url = this.lesson.pdfUrl;
    const hasHash = url.includes('#');
    const pdfUrlWithParams = hasHash
      ? `${url}&toolbar=0&navpanes=1&scrollbar=0&view=FitH`
      : `${url}#toolbar=0&navpanes=1&scrollbar=0&view=FitH`;

    return this.sanitizer.bypassSecurityTrustResourceUrl(pdfUrlWithParams);
  }

  protected onPdfLoad(): void {
    if (this.pdfIframe?.nativeElement) {
      // Aguardar um pouco para garantir que o PDF viewer tenha carregado completamente
      setTimeout(() => {
        this.applyPdfCustomizations(this.pdfIframe.nativeElement);
      }, 500);
    }
  }

  private applyPdfCustomizations(iframe: HTMLIFrameElement): void {
    try {
      const iframeDoc =
        iframe.contentDocument || iframe.contentWindow?.document;
      if (!iframeDoc) return;

      // Criar ou atualizar style tag
      let style = iframeDoc.getElementById(
        'pdf-custom-styles',
      ) as HTMLStyleElement;
      if (!style) {
        style = iframeDoc.createElement('style');
        style.id = 'pdf-custom-styles';
        iframeDoc.head.appendChild(style);
      }

      // CSS para ocultar download e aplicar overflow
      style.textContent = `
        /* Ocultar botão de download */
        button[id*="download"],
        button[title*="Download"],
        button[title*="download"],
        .download,
        [id*="download"] {
          display: none !important;
        }
        
        /* Aplicar overflow customizado */
        body {
          overflow-y: auto;
          overflow-x: auto;
        }
        
        /* Scrollbar customizada - Light mode */
        *::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        
        *::-webkit-scrollbar-track {
          background: transparent;
        }
        
        *::-webkit-scrollbar-thumb {
          background-color: rgb(148 163 184 / 0.5);
          border-radius: 4px;
          border: 2px solid transparent;
          background-clip: padding-box;
        }
        
        *::-webkit-scrollbar-thumb:hover {
          background-color: rgb(148 163 184 / 0.7);
        }
      `;
    } catch (error) {
      // Silenciar erro de cross-origin se necessário
      console.warn('Não foi possível aplicar customizações no PDF:', error);
    }
  }
}
